import { useRouter, useLocation } from '@tanstack/react-router';
import { AppRoute } from '@agentselly/immodossier-shared';
import { useTranslate } from '@tolgee/react';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { useRef } from 'react';
import { executeAppBack } from '~/services/AppBackService';

interface BackButtonProps {
  readonly className?: string;
  /**
   * Fallback route the button navigates to when there is no valid from-state
   * (e.g. deep link / fresh tab). If omitted, the app root '/' is used.
   */
  readonly fallbackTo?: AppRoute | string;
  readonly fallbackParams?: Record<string, string>;
  /**
   * Optional URL hash fragment (e.g. "#evaluationPropertyInformation")
   * appended to the fallback route. Only used when navigating via fallback.
   */
  readonly fallbackHash?: string;
}

export function BackButton({
  className,
  fallbackTo,
  fallbackParams,
  fallbackHash,
}: BackButtonProps) {
  const router = useRouter();
  const location = useLocation();
  const { t } = useTranslate();

  // Set on pointerdown so the synthetic click that follows a touch is ignored
  // (otherwise a touch would navigate back twice).
  const handledByPointerRef = useRef(false);

  const goBack = () => {
    // A focused form field keeps the mobile soft-keyboard open. Blur it first
    // so the keyboard-dismiss reflow cannot swallow / mis-target this tap.
    if (
      typeof document !== 'undefined' &&
      document.activeElement instanceof HTMLElement
    ) {
      document.activeElement.blur();
    }

    // AppBackService: navigate to the from-state location using TanStack
    // router.navigate with replace semantics. This reads the validated `from`
    // field from the current TanStack location state.
    executeAppBack(
      location,
      (options) => {
        void router.navigate(options);
      },
      {
        fallbackTo: fallbackTo as any,
        fallbackParams,
        fallbackHash,
      },
    );
  };

  // Touch/pen: act on pointerdown, BEFORE the keyboard-dismiss reflow moves the
  // button out from under the finger. Mouse keeps using click so a press that
  // drags off the button can still be cancelled.
  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType === 'mouse') {
      return;
    }
    handledByPointerRef.current = true;
    goBack();
  };

  // Fires for mouse and keyboard (Enter/Space). Skip if a preceding pointerdown
  // already navigated for this touch.
  const handleClick = () => {
    if (handledByPointerRef.current) {
      handledByPointerRef.current = false;
      return;
    }
    goBack();
  };

  return (
    <button
      type="button"
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      aria-label={t('back_to_overview')}
      className={clsx(
        // 48x48px touch target (comfortably above the 44px Apple HIG / Material
        // minimum); -m-1.5 keeps the visual footprint compact.
        // touch-manipulation removes the ~300ms tap delay + double-tap zoom on
        // mobile; the active: state gives immediate tactile feedback so a tap
        // never feels dropped.
        'flex h-12 w-12 -m-1.5 shrink-0 touch-manipulation select-none items-center justify-center rounded-full text-gray-500 transition duration-100 hover:bg-primary-50 active:scale-90 active:bg-primary-100 dark:text-gray-400 dark:hover:bg-gray-800 dark:active:bg-gray-700',
        className,
      )}
      title={t('back_to_overview')}
    >
      <ChevronLeftIcon className="w-5 h-5" />
    </button>
  );
}
